import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Alert, Spinner, Modal } from "react-bootstrap";
import api from "../../api";
import apiPublic from "../../apiPublic";
import DynamicFormRenderer, { isFieldVisible } from "../../components/forms/DynamicFormRenderer";
import { viewAuthenticatedFileInNewTab, viewPublicFileInNewTab, getFileAccessErrorMessage } from "../../utils/fileAccess";
import { toast } from "react-toastify";
import "./FormFillContent.css";

// One read-only "Assignment Details" cell. The leading icon is only drawn
// on the public link page (`showIcon`), whose branded layout uses it; the
// authenticated fill page keeps its plain label/value cells.
function AssignmentDetail({ icon, label, wide = false, showIcon = false, children }) {
  return (
    <div className={`ff-assignment-detail${wide ? " ff-assignment-detail-wide" : ""}`}>
      {showIcon && <i className={`bi bi-${icon} ff-detail-icon`} aria-hidden="true" />}
      <div className="ff-assignment-detail-body">
        <span>{label}</span>
        <strong>{children}</strong>
      </div>
    </div>
  );
}

// Phase 3B, §25. Reuses DynamicFormRenderer in "fill" mode — no separate
// rendering logic. Same evaluateCondition/isFieldVisible pair as preview;
// the backend independently re-evaluates visibility and validates at
// submit time, this is UX only.
// Mirrors formSubmissionValueService.js's NO_VALUE_TYPES on the backend --
// these field types never carry a real answer, so they're never checked
// for "required" here or there.
const NO_VALUE_TYPES = new Set(["SECTION_HEADING", "STATIC_TEXT", "CALCULATED", "FILE_UPLOAD", "IMAGE_UPLOAD", "REPEATING_TABLE"]);

// Mirrors formSubmissionValidationService.js's isEmpty() exactly -- type-
// aware so a valid numeric 0, boolean false, or non-empty array is never
// wrongly treated as unanswered; only undefined/null/''/[] (and a
// whitespace-only string) count as empty.
function isAnswerEmpty(value) {
  if (value === undefined || value === null) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "string") return value.trim() === "";
  return false;
}

function useRolePrefix() {
  const path = window.location.pathname;
  const match = path.match(/^\/([^/]+)\//);
  return match ? match[1] : "admin";
}

// Distinguishes WHY the form failed to load instead of always showing the
// same generic sentence -- 401/403/404/409/422/500 are genuinely different
// situations for the user (and for whoever has to debug a report of "it
// doesn't work"). The backend's own message/code (and, outside production,
// its `details`) is always preferred when present; these are only the
// fallback for a response with no message at all.
function describeLoadError(err) {
  const status = err?.response?.status;
  const serverMessage = err?.response?.data?.message;
  const code = err?.response?.data?.code;
  const details = err?.response?.data?.details; // dev-only, see utils/apiError.js
  const fallback =
    status === 401 ? "Your session has expired. Please sign in again."
    : status === 403 ? "You do not have access to this assigned form."
    : status === 404 ? "This assignment, form, version, or submission was not found."
    : status === 409 ? "This form's current state does not allow filling right now."
    : status === 422 ? "This form's configuration is invalid and cannot be filled."
    : status === 500 ? "A server error occurred while loading this form."
    : "Failed to load this form.";
  return { status, code, message: serverMessage || fallback, details };
}

// Shared business content for Form Fill -- runtime loading, values,
// validation, Save Draft, Submit, project/module metadata display,
// DynamicFormRenderer, progress, and file handling all live here,
// unchanged. AdminFormFillPage.jsx and RoleFormFillPage.jsx are thin
// presentation wrappers that both render this same component for the
// authenticated flow.
//
// Phase 16 (Public Form Links), §23: src/pages/public/PublicFormPage.jsx
// renders this SAME component for the no-login public-link flow via
// `mode="public"` + `token` — no PublicFormFillContent.jsx duplicate exists.
// Every API call below is routed through a tiny per-mode path/client
// switch (private unchanged, public token-scoped) rather than branching
// business logic; default props keep the authenticated path byte-for-byte
// identical to before this change.
export default function FormFillContent({ mode = "private", token = null, initialRuntime = null, respondentEmail = null, respondentName = null } = {}) {
  // assignmentUserId is part of the route shape for readability/back-nav
  // symmetry with MyAssignedFormsPage, but every lookup here keys off the
  // submission id — the runtime endpoint already resolves back to the
  // owning assignment-user server-side. Unused (and harmless) in public
  // mode, where useParams() has no submissionId to find.
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const rolePrefix = useRolePrefix();
  const isPublic = mode === "public";

  // Public mode uses apiPublic (no auth header, no session-expiry redirect)
  // against token-scoped paths that never contain a submissionId; private
  // mode is untouched — same client, same paths as before this change.
  const client = isPublic ? apiPublic : api;
  const runtimePath = isPublic ? `/public/form-access/${token}` : `/form-submissions/${submissionId}/runtime`;
  const draftPath = isPublic ? `/public/form-access/${token}/draft` : `/form-submissions/${submissionId}/draft`;
  const submitPath = isPublic ? `/public/form-access/${token}/submit` : `/form-submissions/${submissionId}/submit`;
  const filesPath = isPublic ? `/public/form-access/${token}/files` : `/form-submissions/${submissionId}/files`;
  const fileDeletePath = (fileId) => (isPublic ? `/public/form-access/${token}/files/${fileId}` : `/form-submissions/${submissionId}/files/${fileId}`);
  const fileViewPath = (fileId) => (isPublic ? `/public/form-access/${token}/files/${fileId}/view` : `/form-submissions/${submissionId}/files/${fileId}/view`);

  const [runtime, setRuntime] = useState(null);
  const [loading, setLoading] = useState(!initialRuntime);
  const [loadError, setLoadError] = useState(null);

  const [values, setValues] = useState({});
  const [repeatingRows, setRepeatingRows] = useState({});
  const [removedRowIds, setRemovedRowIds] = useState({});
  const [filesByFieldKey, setFilesByFieldKey] = useState({});
  const [filesByRow, setFilesByRow] = useState({});

  const [fieldErrors, setFieldErrors] = useState({});
  const [summaryErrors, setSummaryErrors] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Debounced draft auto-save (~1.2s after the last change) -- distinct
  // from `saving` (which only reflects a manually-clicked Save Draft or the
  // implicit save-before-submit) so the small "Saving.../Saved/Save failed"
  // indicator only ever reflects the auto-save path. Reuses the exact same
  // saveDraft()/draft endpoint as the manual button -- no second draft
  // mechanism. null until the first auto-save attempt.
  const [autosaveStatus, setAutosaveStatus] = useState(null);
  const autosaveTimerRef = useRef(null);
  const autosaveRunRef = useRef(null);

  // Duplicate-submission guard: `submitting` (state) disables the button,
  // but a state update is asynchronous -- two clicks fired in the same
  // event-loop tick before React re-renders would both pass the
  // `disabled={submitting}` check. This ref is set/read synchronously so
  // a genuine double-click (or a repeated programmatic call) can only ever
  // enter submit() once per in-flight request.
  const isSubmittingRef = useRef(false);

  // Success confirmation modal (public link only -- see submit() below).
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null);

  const tempIdCounter = useRef(0);

  // Moved above the auto-save/flush-on-hide effects below (both need it),
  // otherwise unchanged from its original position/logic.
  const isReadOnly = !runtime?.submission?.is_editable;

  // Shared by loadRuntime()'s own fetch AND by the initialRuntime-seeded
  // mount below (Phase 16, §22/§23: PublicFormPage.jsx does its OWN first
  // fetch of GET /public/form-access/:token so it can render dedicated
  // invalid/expired/revoked screens instead of this component's generic
  // error Alert; on success it hands the already-fetched payload in here
  // via `initialRuntime` rather than this component re-fetching it a
  // second time).
  const applyRuntimeData = useCallback((data) => {
    setRuntime(data);
    setValues(data?.valuesByFieldKey || {});
    setRepeatingRows(data?.repeatingRowsBySection || {});
    setRemovedRowIds({});
    setFilesByFieldKey(data?.filesByFieldKey || {});
    setFilesByRow(data?.filesByRow || {});
    setDirty(false);
    setFieldErrors({});
    setSummaryErrors([]);
    setAutosaveStatus(null);
  }, []);

  const loadRuntime = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const res = await client.get(runtimePath);
      applyRuntimeData(res.data?.data);
    } catch (err) {
      console.error("Fetch form runtime error:", err);
      setLoadError(describeLoadError(err));
    } finally {
      setLoading(false);
    }
  }, [client, runtimePath, applyRuntimeData]);

  useEffect(() => {
    if (initialRuntime) {
      applyRuntimeData(initialRuntime);
      setLoading(false);
      return;
    }
    loadRuntime();
    // Only ever re-runs when the thing being fetched changes; initialRuntime
    // is only meant to seed the VERY FIRST mount, not re-trigger on re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRuntime]);

  // Warn before leaving with unsaved changes (§25).
  useEffect(() => {
    const handler = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // Best-effort "save immediately before navigation" -- fires the EXISTING
  // silent draft save the moment the tab is backgrounded/closed (tab
  // switch, minimize, navigating away), while the page is still alive to
  // let the in-flight request complete. Not guaranteed (a hard browser
  // close can still race it), hence "best effort" -- but far more likely
  // to land than anything relying on `beforeunload` alone, and needs no
  // new endpoint/mechanism (still just saveDraft()).
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== "hidden") return;
      if (!dirty || isReadOnly || !runtime?.assignment?.allow_save_draft) return;
      saveDraft({ silent: true });
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
    // Re-attaches on every values/repeatingRows/removedRowIds change (not
    // just dirty/isReadOnly/runtime) so this always closes over the LATEST
    // saveDraft/state -- otherwise a tab hidden after several keystrokes
    // with dirty already true could flush a stale, outdated snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, isReadOnly, runtime, values, repeatingRows, removedRowIds]);

  // Keep the latest auto-save runner in a ref so the debounce effect below
  // only needs to depend on the actual field-change signals (values/
  // repeatingRows/removedRowIds) -- not on `saveDraft` itself, which would
  // otherwise re-arm the debounce timer on every unrelated render.
  autosaveRunRef.current = async () => {
    setAutosaveStatus("saving");
    const ok = await saveDraft({ silent: true });
    setAutosaveStatus(ok ? "saved" : "failed");
  };

  // Debounced draft auto-save: ~1.2s of no further changes after a field is
  // edited, not on every keystroke. Reuses saveDraft()/the existing draft
  // endpoint -- no new API, no new table. Skips entirely while read-only,
  // while a manual save/submit is already in flight, or when this
  // assignment doesn't allow draft saves at all.
  useEffect(() => {
    if (!dirty || isReadOnly || submitting || !runtime?.assignment?.allow_save_draft) return undefined;
    autosaveTimerRef.current = setTimeout(() => {
      autosaveRunRef.current?.();
    }, 1200);
    return () => clearTimeout(autosaveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, repeatingRows, removedRowIds, dirty, isReadOnly, submitting]);

  const fieldByKey = useMemo(() => {
    const map = {};
    (runtime?.structure?.fields || []).forEach((f) => { map[f.field_key] = f; });
    return map;
  }, [runtime]);

  // The assignment itself already fixes a project/module (see the
  // "Project:"/"Module:" chips below) -- a PROJECT_SELECT/PROJECT_MODULE_SELECT
  // field on the form is a generic, reusable field type with no built-in
  // knowledge of that context, so without this it always starts blank and
  // makes the filler re-pick something the assignment already decided.
  // Only ever SEEDS an empty value once per load; never overwrites a value
  // the filler (or a prior draft save) already set.
  useEffect(() => {
    if (!runtime) return;
    const projectField = (runtime.structure?.fields || []).find((f) => f.field_type === "PROJECT_SELECT");
    const moduleField = (runtime.structure?.fields || []).find((f) => f.field_type === "PROJECT_MODULE_SELECT");
    const seed = {};
    if (projectField && runtime.assignment?.project_id && !values[projectField.field_key]) {
      seed[projectField.field_key] = String(runtime.assignment.project_id);
    }
    if (moduleField && runtime.assignment?.project_module_id && !values[moduleField.field_key]) {
      seed[moduleField.field_key] = String(runtime.assignment.project_module_id);
    }
    if (Object.keys(seed).length > 0) {
      setValues((prev) => ({ ...seed, ...prev }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime]);

  // Tells DynamicFormRenderer to skip PROJECT_SELECT/PROJECT_MODULE_SELECT
  // entirely (their value is already fixed by the assignment, shown below
  // in the read-only Assignment Details block -- see item 1/17 of this
  // page's redesign). Built purely from data already in `runtime`; never
  // fetched separately, never hardcoded.
  const assignmentProjectContext = useMemo(() => {
    if (!runtime) return null;
    return {
      projectId: runtime.assignment?.project_id ?? null,
      projectName: runtime.assignment?.project_name || null,
      moduleId: runtime.assignment?.project_module_id ?? null,
      moduleName: runtime.assignment?.project_module_name || null,
    };
  }, [runtime]);

  // Knowledge Sharing subject-selection hardening: tells DynamicFormRenderer
  // whether THIS submission's subject field (is_subject_field, e.g.
  // "Employee / Presenter Being Evaluated") is locked
  // (form_assignments.locked_subject_user_id) and, if so, who it resolved to
  // -- built purely from the runtime payload already fetched above
  // (`runtime.subject`/`runtime.subject_locked`), never a second fetch.
  // `submissionId` is only meaningful for the authenticated/portal flow
  // (useParams() has no submissionId on the public-link flow, isPublic) --
  // the real employee-search endpoint this hardening adds is scoped to a
  // portal submissionId, so the public flow keeps its existing plain-text
  // fallback behavior for an unlocked subject field (requirement 6).
  const subjectContext = useMemo(() => {
    if (!runtime) return null;
    return {
      submissionId: isPublic ? null : submissionId,
      locked: !!runtime.subject_locked,
      subject: runtime.subject || null,
    };
  }, [runtime, isPublic, submissionId]);

  // Same due-date-passed criterion already used for assignment lists
  // elsewhere in the app (due date in the past AND still open) -- not a
  // new/independent overdue rule, just applied here for display.
  const isOverdue = !isReadOnly && !!runtime?.assignment?.due_at && new Date(runtime.assignment.due_at) < new Date();

  // Presentational only -- a rough "how far along" indicator for the
  // compact header (top-level, non-repeating-section fields answered vs
  // total). The backend independently re-validates required fields at
  // submit time (see applyValidationErrors below); this never gates
  // submission or duplicates that validation logic.
  const progress = useMemo(() => {
    const fields = (runtime?.structure?.fields || []).filter((f) => {
      const section = (runtime?.structure?.sections || []).find((s) => s.id === f.form_section_id);
      return !section?.is_repeatable;
    });
    const answered = fields.filter((f) => {
      const v = values[f.field_key];
      return v !== undefined && v !== null && v !== "";
    }).length;
    return { answered, total: fields.length };
  }, [runtime, values]);

  const handleValueChange = (fieldKey, value) => {
    setValues((prev) => ({ ...prev, [fieldKey]: value }));
    setDirty(true);
    // Clear this field's inline "required" error the moment it gets a
    // valid answer, without waiting for the next Submit attempt.
    if (fieldErrors[fieldKey] && !isAnswerEmpty(value)) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      });
    }
  };

  const handleRepeatingRowValueChange = (sectionId, rowId, fieldKey, value) => {
    setRepeatingRows((prev) => ({
      ...prev,
      [sectionId]: (prev[sectionId] || []).map((row) => (row.id === rowId ? { ...row, values: { ...row.values, [fieldKey]: value } } : row)),
    }));
    setDirty(true);
  };

  const handleAddRepeatingRow = (sectionId) => {
    tempIdCounter.current += 1;
    const tempId = `temp-${tempIdCounter.current}`;
    setRepeatingRows((prev) => ({
      ...prev,
      [sectionId]: [...(prev[sectionId] || []), { id: tempId, isTemp: true, row_key: null, values: {} }],
    }));
    setDirty(true);
  };

  const handleRemoveRepeatingRow = (sectionId, rowId) => {
    const row = (repeatingRows[sectionId] || []).find((r) => r.id === rowId);
    if (!row) return;
    setRepeatingRows((prev) => ({ ...prev, [sectionId]: (prev[sectionId] || []).filter((r) => r.id !== rowId) }));
    if (!row.isTemp) {
      setRemovedRowIds((prev) => ({ ...prev, [sectionId]: [...(prev[sectionId] || []), rowId] }));
    }
    setDirty(true);
  };

  const handleUploadFile = async (field, file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("form_field_id", field.id);
    try {
      const res = await client.post(filesPath, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setFilesByFieldKey((prev) => ({ ...prev, [field.field_key]: [...(prev[field.field_key] || []), res.data?.data] }));
      toast.success("File uploaded");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to upload file");
    }
  };

  const handleUploadRowFile = async (rowId, field, file) => {
    if (typeof rowId === "string" && rowId.startsWith("temp-")) {
      toast.warning("Save the form first, then upload a file for this row.");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    formData.append("form_field_id", field.id);
    formData.append("repeating_row_id", rowId);
    try {
      const res = await client.post(filesPath, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setFilesByRow((prev) => ({
        ...prev,
        [rowId]: { ...(prev[rowId] || {}), [field.field_key]: [...((prev[rowId] || {})[field.field_key] || []), res.data?.data] },
      }));
      toast.success("File uploaded");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to upload file");
    }
  };

  const handleDeleteFile = async (file) => {
    if (!window.confirm(`Remove "${file.original_name}"?`)) return;
    try {
      await client.delete(fileDeletePath(file.id));
      setFilesByFieldKey((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((k) => { next[k] = next[k].filter((f) => f.id !== file.id); });
        return next;
      });
      setFilesByRow((prev) => {
        const next = {};
        Object.keys(prev).forEach((rowId) => {
          next[rowId] = {};
          Object.keys(prev[rowId]).forEach((k) => { next[rowId][k] = prev[rowId][k].filter((f) => f.id !== file.id); });
        });
        return next;
      });
      toast.success("File removed");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to remove file");
    }
  };

  const handleViewFile = async (file) => {
    try {
      if (isPublic) {
        await viewPublicFileInNewTab(fileViewPath(file.id));
      } else {
        await viewAuthenticatedFileInNewTab(fileViewPath(file.id));
      }
    } catch (err) {
      toast.error(getFileAccessErrorMessage(err));
    }
  };

  const buildDraftPayload = () => {
    const valuesPayload = Object.keys(fieldByKey)
      .filter((fieldKey) => {
        const field = fieldByKey[fieldKey];
        const section = (runtime.structure.sections || []).find((s) => s.id === field.form_section_id);
        return section && !section.is_repeatable;
      })
      .filter((fieldKey) => values[fieldKey] !== undefined)
      .map((fieldKey) => ({ form_field_id: fieldByKey[fieldKey].id, value: values[fieldKey] }));

    const repeatingRowsPayload = [];
    Object.entries(repeatingRows).forEach(([sectionId, rows]) => {
      rows.forEach((row) => {
        const rowValues = Object.entries(row.values || {}).map(([fieldKey, value]) => ({ form_field_id: fieldByKey[fieldKey]?.id, value })).filter((v) => v.form_field_id);
        if (row.isTemp) {
          repeatingRowsPayload.push({ form_section_id: Number(sectionId), row_key: row.row_key, values: rowValues });
        } else {
          repeatingRowsPayload.push({ id: row.id, form_section_id: Number(sectionId), values: rowValues });
        }
      });
      (removedRowIds[sectionId] || []).forEach((rowId) => {
        repeatingRowsPayload.push({ id: rowId, form_section_id: Number(sectionId), is_deleted: true, values: [] });
      });
    });

    return { values: valuesPayload, repeating_rows: repeatingRowsPayload };
  };

  const saveDraft = async ({ silent = false } = {}) => {
    setSaving(true);
    try {
      // Public Form Respondent Identity: `email` rides along on every
      // draft save (public mode only) -- the backend re-verifies it
      // matches this token's own assignee on every call, never just once
      // up front (see controllers/publicFormAccessController.js#saveDraft).
      const payload = isPublic ? { ...buildDraftPayload(), email: respondentEmail } : buildDraftPayload();
      await client.put(draftPath, payload);
      setDirty(false);
      if (!silent) toast.success("Draft saved");
      await loadRuntime();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save draft");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const scrollToField = (fieldKey) => {
    if (!fieldKey) return;
    // Deferred a frame so the just-set fieldErrors/isInvalid styling has
    // already painted before we scroll -- otherwise layout can shift under
    // the scroll target on the very first paint.
    requestAnimationFrame(() => {
      const el = document.getElementById(`ff-field-${fieldKey}`);
      if (!el) return;
      el.scrollIntoView?.({ behavior: "smooth", block: "center" });
      el.querySelector("input, textarea, select")?.focus();
    });
  };

  const applyValidationErrors = (details) => {
    const byField = {};
    const summary = [];
    (details || []).forEach((d) => {
      summary.push(d.message);
      if (d.field_key && !byField[d.field_key]) byField[d.field_key] = d.message;
    });
    setFieldErrors(byField);
    setSummaryErrors(summary);
    scrollToField(details?.find((d) => d.field_key)?.field_key);
  };

  // UX-only pre-submit check, mirroring formSubmissionValidationService.js's
  // required-field rule (isEmpty + is_required) and DynamicFormRenderer's
  // own isFieldVisible -- so a field hidden by the current answers is never
  // flagged here either. The backend re-validates independently and
  // authoritatively at submit time regardless of what this finds; this only
  // exists so an incomplete form never has to make a round trip to learn
  // that. Repeating-section rows are checked too (summary message only --
  // scrolling to a specific row's field is a larger change than this fix
  // needs, since the reported case is a plain top-level question).
  const validateRequiredFields = () => {
    const fields = runtime?.structure?.fields || [];
    const sections = runtime?.structure?.sections || [];
    const byField = {};
    const summary = [];
    let firstInvalidFieldKey = null;

    const fieldsBySection = new Map();
    fields.forEach((f) => {
      if (!fieldsBySection.has(f.form_section_id)) fieldsBySection.set(f.form_section_id, []);
      fieldsBySection.get(f.form_section_id).push(f);
    });

    sections.forEach((section) => {
      if (!section.is_active) return;
      const sectionFields = fieldsBySection.get(section.id) || [];

      if (!section.is_repeatable) {
        sectionFields.forEach((field) => {
          if (NO_VALUE_TYPES.has(field.field_type) || !field.is_required) return;
          if (!isFieldVisible(field, values)) return;
          if (isAnswerEmpty(values[field.field_key])) {
            const message = "This question is required.";
            if (!byField[field.field_key]) byField[field.field_key] = message;
            summary.push(`"${field.label}" is required.`);
            if (!firstInvalidFieldKey) firstInvalidFieldKey = field.field_key;
          }
        });
        return;
      }

      (repeatingRows[section.id] || []).forEach((row, rowIndex) => {
        const rowValues = { ...values, ...(row.values || {}) };
        sectionFields.forEach((field) => {
          if (NO_VALUE_TYPES.has(field.field_type) || !field.is_required) return;
          if (!isFieldVisible(field, rowValues)) return;
          if (isAnswerEmpty((row.values || {})[field.field_key])) {
            summary.push(`"${section.title}" row ${rowIndex + 1}: "${field.label}" is required.`);
          }
        });
      });
    });

    return { byField, summary, firstInvalidFieldKey };
  };

  const submit = async () => {
    // Synchronous re-entrancy guard -- a double-click (or a second
    // programmatic call) that lands before `submitting` state has even
    // re-rendered the disabled button is rejected here instead.
    if (isSubmittingRef.current) return;

    setFieldErrors({});
    setSummaryErrors([]);

    const { byField, summary, firstInvalidFieldKey } = validateRequiredFields();
    if (summary.length > 0) {
      setFieldErrors(byField);
      setSummaryErrors(summary);
      toast.error("Please complete all required questions before submitting.");
      scrollToField(firstInvalidFieldKey);
      return; // never calls the API with a known-incomplete form
    }

    isSubmittingRef.current = true;
    setSubmitting(true);
    try {
      const saved = await saveDraft({ silent: true });
      if (!saved) return;
      // Public Form Respondent Identity: the attribution-critical gate --
      // the backend rejects this call if `email` doesn't match the
      // token's own assignee, regardless of what saveDraft above already
      // succeeded with.
      const submitRes = await client.post(submitPath, isPublic ? { email: respondentEmail } : {});
      setDirty(false);
      // Public mode has no authenticated "submission view" route to send an
      // anonymous visitor to -- reload the SAME page instead, which now
      // renders read-only (runtime.submission.is_editable becomes false).
      // A confirmation MODAL (not just a toast) is shown here, and stays
      // open until the visitor explicitly closes it -- an important final
      // submission shouldn't rely on a toast the visitor might not notice
      // before it disappears. The toast is kept too, per spec, as a
      // secondary/supplementary signal only.
      if (isPublic) {
        const submittedSubmission = submitRes?.data?.data || null;
        setSuccessInfo({
          formName: runtime?.form?.name || null,
          submissionId: submittedSubmission?.id ?? runtime?.submission?.id ?? null,
          submissionNumber: submittedSubmission?.submission_number ?? runtime?.submission?.submission_number ?? null,
          submittedAt: submittedSubmission?.submitted_at || new Date().toISOString(),
        });
        toast.success("Form submitted successfully");
        setShowSuccessModal(true);
        await loadRuntime();
      } else {
        toast.success("Form submitted");
        navigate(`/${rolePrefix}/form-submissions/${submissionId}`);
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "FORM_SUBMISSION_VALIDATION_FAILED") {
        applyValidationErrors(data.details);
        toast.error("Please fix the highlighted issues before submitting.");
      } else if (data?.code === "FORM_ASSIGNMENT_OVERDUE") {
        toast.error("The due date for this form has passed and late submissions are not allowed.");
      } else if (data?.code === "FORM_ASSIGNMENT_INACTIVE") {
        toast.error(data?.message || "This assignment is no longer open for submissions.");
      } else if (data?.code === "FORM_SUBMISSION_IMMUTABLE") {
        toast.error(data?.message || "This submission has already been submitted and can no longer be edited.");
      } else {
        toast.error(data?.message || "Failed to submit form");
      }
      // Deliberately no success modal/navigation here, and every entered
      // value is left exactly as-is (nothing in this catch touches
      // values/repeatingRows) -- the visitor can fix the issue and press
      // Submit again without retyping anything.
    } finally {
      isSubmittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="unit-page"><div className="unit-loader"><Spinner animation="border" /><p>Loading form...</p></div></div>;
  }
  if (loadError || !runtime) {
    return (
      <div className="unit-page">
        <Alert variant="danger">
          {loadError?.message || "This form could not be loaded."}
          {/* Error codes/details are diagnostic for an authenticated internal
              user -- never shown to an anonymous public-link visitor. */}
          {!isPublic && loadError?.code ? <div className="text-muted small mt-1">Error code: {loadError.code}</div> : null}
          {!isPublic && loadError?.details ? <div className="text-muted small mt-1">Details: {loadError.details}</div> : null}
        </Alert>
        {!isPublic && (
          <Button className="unit-btn unit-btn-light" style={{ marginTop: 12 }} onClick={() => navigate(`/${rolePrefix}/my-assigned-forms`)}>Back to My Assigned Forms</Button>
        )}
      </div>
    );
  }

  return (
    <div className="unit-page ff-page-theme">

      <div className="unit-header ff-header">
        {isPublic && (
          <div className="ff-hero-icon" aria-hidden="true">
            <i className="bi bi-chat-square-text" />
          </div>
        )}
        <div className="ff-header-text">
          <div className="unit-page-label">{runtime.form.form_type_name}</div>
          <h2>{runtime.form.name}</h2>
          {runtime.assignment.related_task_id ? (
            // Task/complaint-linked feedback: the auto-generated
            // assignment_title ("Feedback: Task #467" -- see
            // taskSatisfactionService.js) is an internal placeholder, never
            // meant for the respondent to read. Once real task context is
            // available (related_task_id set), show what it's actually
            // feedback FOR instead of that placeholder string.
            <>
              <p className="ff-assignment-sub">Feedback for: {runtime.assignment.related_task_title || "this task"}</p>
              {!isPublic && (
                <p className="ff-assignment-sub">Complaint <span className="ff-sub-value">#{runtime.assignment.related_task_id}</span></p>
              )}
            </>
          ) : (
            runtime.assignment.title && <p className="ff-assignment-sub">Assignment: {runtime.assignment.title}</p>
          )}
          {isPublic && respondentName && (
            <p className="ff-assignment-sub">Submitting as: <span className="ff-sub-value">{respondentName}</span></p>
          )}
        </div>
        {isPublic && (
          <div className="ff-hero-art" aria-hidden="true">
            <i className="bi bi-clipboard2-check" />
          </div>
        )}
        {!isPublic && (
          <div className="unit-header-actions">
            <Button size="sm" className="unit-btn unit-btn-light ff-back-btn" onClick={() => navigate(`/${rolePrefix}/my-assigned-forms`)}>← Back</Button>
          </div>
        )}
      </div>

      {isPublic && isReadOnly && runtime.submission.status === "SUBMITTED" && (
        <Alert variant="success" className="ff-public-submitted-alert">
          <strong>This form has already been submitted.</strong>
          {runtime.submission.submitted_at && (
            <div className="small">Submitted: {new Date(runtime.submission.submitted_at).toLocaleString()}</div>
          )}
          <div className="small">Your response has been recorded.</div>
        </Alert>
      )}

      {/* Public link: no Version/status chips (respondent-facing page); the
          row only renders there when some other chip applies. */}
      {(!isPublic || runtime.assignment.assigned_by_name || runtime.assignment.project_name
        || runtime.assignment.project_module_name || runtime.assignment.due_at || isOverdue) && (
      <div className="ff-meta-chips">
        {!isPublic && <span className="ff-chip">Version {runtime.form.version_number}</span>}
        {runtime.assignment.assigned_by_name && (
          <span className="ff-chip">Created by: {runtime.assignment.assigned_by_name}</span>
        )}
        {runtime.assignment.project_name && (
          <span className="ff-chip">Project: {runtime.assignment.project_name}</span>
        )}
        {runtime.assignment.project_module_name && (
          <span className="ff-chip">Module: {runtime.assignment.project_module_name}</span>
        )}
        {runtime.assignment.due_at && (
          <span className="ff-chip">Due: {new Date(runtime.assignment.due_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}</span>
        )}
        {isOverdue && <span className="ff-chip ff-chip-overdue">Overdue</span>}
        {!isPublic && (
          <span className={`ff-chip ff-chip-status ${isReadOnly ? "ff-chip-status-done" : "ff-chip-status-open"}`}>
            {runtime.submission.status}
          </span>
        )}
      </div>
      )}

      {summaryErrors.length > 0 && (
        <Alert variant="danger">
          <strong>Please fix the following before submitting:</strong>
          <ul className="mb-0">{summaryErrors.map((m, i) => <li key={i}>{m}</li>)}</ul>
        </Alert>
      )}

      <div className="unit-card ff-form-card">
        <div className="ff-assignment-details">
          <div className="ff-assignment-details-title">
            {isPublic && <i className="bi bi-file-earmark-text" aria-hidden="true" />}
            Assignment Details
          </div>
          <div className="ff-assignment-details-grid">
            {runtime.assignment.related_task_id ? (
              // Task/complaint-linked feedback (e.g. IT_SUPPORT satisfaction
              // requests -- see services/taskSatisfactionService.js): these
              // assignments never set project_id/project_module_id, so the
              // generic Project/Module/Assigned By fields would only ever
              // read "Not specified". The actual task carries real context,
              // so show that instead. Technician comes from `runtime.subject`
              // (resolved server-side from locked_subject_user_id -- see
              // formRuntimeService.js), never re-resolved here.
              <>
                <AssignmentDetail icon="card-text" label="Title" showIcon={isPublic}>
                  {runtime.assignment.related_task_title || "Not specified"}
                </AssignmentDetail>
                <AssignmentDetail icon="grid" label="Category" showIcon={isPublic}>
                  {runtime.assignment.related_task_category_name || "Not specified"}
                </AssignmentDetail>
                <AssignmentDetail icon="chat-square-dots" label="Subcategory" showIcon={isPublic}>
                  {runtime.assignment.related_task_sub_category_name || "Not specified"}
                </AssignmentDetail>
                <AssignmentDetail icon="person" label="Technician" showIcon={isPublic}>
                  {runtime.subject?.name || "Not specified"}
                </AssignmentDetail>
                {runtime.assignment.related_task_due_at && (
                  <AssignmentDetail icon="calendar3" label="Due Date" showIcon={isPublic}>
                    {new Date(runtime.assignment.related_task_due_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
                  </AssignmentDetail>
                )}
              </>
            ) : (
              <>
                <AssignmentDetail icon="kanban" label="Project" showIcon={isPublic}>
                  {runtime.assignment.project_name || "Not specified"}
                </AssignmentDetail>
                <AssignmentDetail icon="grid" label="Module / Feature" showIcon={isPublic}>
                  {runtime.assignment.project_module_name || "Not specified"}
                </AssignmentDetail>
                <AssignmentDetail icon="person" label="Assigned By" showIcon={isPublic}>
                  {runtime.assignment.assigned_by_name || "Not specified"}
                </AssignmentDetail>
                <AssignmentDetail icon="calendar3" label="Due Date" showIcon={isPublic}>
                  {runtime.assignment.due_at
                    ? new Date(runtime.assignment.due_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
                    : "Not specified"}
                  {isOverdue && <span className="ff-overdue-inline">Overdue</span>}
                </AssignmentDetail>
              </>
            )}
            {runtime.assignment.instructions && (
              <AssignmentDetail icon="info-circle" label="Instructions" wide showIcon={isPublic}>
                {runtime.assignment.instructions}
              </AssignmentDetail>
            )}
          </div>
        </div>

        <DynamicFormRenderer
          mode="fill"
          sections={runtime.structure.sections}
          values={values}
          onValueChange={handleValueChange}
          repeatingRowsBySection={repeatingRows}
          onRepeatingRowValueChange={handleRepeatingRowValueChange}
          onAddRepeatingRow={handleAddRepeatingRow}
          onRemoveRepeatingRow={handleRemoveRepeatingRow}
          filesByFieldKey={filesByFieldKey}
          filesByRow={filesByRow}
          onUploadFile={!isReadOnly ? handleUploadFile : undefined}
          onUploadRowFile={!isReadOnly ? handleUploadRowFile : undefined}
          onDeleteFile={!isReadOnly ? handleDeleteFile : undefined}
          onViewFile={handleViewFile}
          fieldErrors={fieldErrors}
          readOnly={isReadOnly}
          executionProfile={runtime?.execution_profile}
          assignmentProjectContext={assignmentProjectContext}
          subjectContext={subjectContext}
        />

        <div className="ff-footer">
          {progress.total > 0 && (
            <div className="ff-progress">
              <span>Progress {progress.answered} / {progress.total}</span>
              <div className="ff-progress-track">
                <div className="ff-progress-fill" style={{ width: `${Math.round((progress.answered / progress.total) * 100)}%` }} />
              </div>
            </div>
          )}
          {!isReadOnly && (
            <div className="ff-footer-actions">
              {runtime.assignment.allow_save_draft && (
                <>
                  <Button className="unit-btn unit-btn-light" disabled={saving || submitting} onClick={() => saveDraft()}>
                    {saving ? "Saving..." : "Save Draft"}
                  </Button>
                  {!saving && autosaveStatus && (
                    <span className={`ff-autosave-status ff-autosave-status-${autosaveStatus}`}>
                      {autosaveStatus === "saving" && "Saving..."}
                      {autosaveStatus === "saved" && "Saved"}
                      {autosaveStatus === "failed" && "Save failed — changes not yet saved"}
                    </span>
                  )}
                </>
              )}
              <Button className="unit-btn unit-btn-primary" disabled={saving || submitting} onClick={submit}>
                {submitting ? "Submitting..." : "Submit Form"}
              </Button>
            </div>
          )}
        </div>
      </div>

      {isPublic && (
        <Modal
          show={showSuccessModal}
          onHide={() => setShowSuccessModal(false)}
          centered
          backdrop="static"
          keyboard={false}
          className="unit-modal ff-success-modal"
        >
          <Modal.Header closeButton closeLabel="Close">
            <Modal.Title>✓ Form submitted successfully</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="ff-success-lead">Your response has been saved.</p>
            <dl className="ff-success-details">
              {successInfo?.formName && (
                <>
                  <dt>Form</dt>
                  <dd>{successInfo.formName}</dd>
                </>
              )}
              {(successInfo?.submissionNumber || successInfo?.submissionId) && (
                <>
                  <dt>Reference</dt>
                  <dd>#{successInfo.submissionNumber ?? successInfo.submissionId}</dd>
                </>
              )}
              {successInfo?.submittedAt && (
                <>
                  <dt>Submitted</dt>
                  <dd>{new Date(successInfo.submittedAt).toLocaleString()}</dd>
                </>
              )}
            </dl>
          </Modal.Body>
          <Modal.Footer>
            <Button className="unit-btn unit-btn-primary" onClick={() => setShowSuccessModal(false)}>Close</Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
